/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-panel.data.ts company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import {
    PanelAggregate,
    PanelAverageLineThresholdType,
    PanelBenchmark,
    PanelColumnType,
    PanelDisplayBy,
    PanelGraphType,
    PanelIntervalType,
    PanelLineCalculation,
    PanelLineTimePeriod,
    PanelOrderBy,
    PanelPlotBy,
    PanelRateBy,
    PanelRateByType,
    PanelSeriesType,
    PanelSiteScoring,
    PanelThresholdType,
    PanelTimePeriod,
} from 'types/graphql-global-types';

export const getPanelInitialState = (studyId: string, organisationId: string) => ({
    id: null as any,
    configId: null,
    studyId,
    organisationId,
    name: '',
    panelType: '',
    weight: '1',
    recordId: null as any,
    active: false,
    includeInSiteScoring: false,
    decision: false,
    updated: '',
    created: '',
    updatedBy: '',
    createdBy: '',
    showStep4: false,
    graph: {
        type: PanelGraphType.HEATMAP,
        plotTitle: '',
        xTitle: '',
        yTitle: '',
        displayBy: PanelDisplayBy.SITE,
        displayUnits: '',
        hideStudyColumns: false,
        orderBy: PanelOrderBy.LABEL_ASCENDING,
        columnType: PanelColumnType.STANDARD,
        categories: [] as any,
        timePeriod: PanelLineTimePeriod.WEEKS,
        seriesType: PanelSeriesType.SITE,
        numberOfSeries: 1,
        plotThreshold: 1000,
        lineCalculation: PanelLineCalculation.MEAN,
        lineThresholdType: PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
        thresholdNum1: 0,
        thresholdNum2: 0,
        // This is used at the end to send the series data to the server
        series: [] as any,

        // These are used as mappings to the input fields, they get converted into the series[] array above
        series1label: '',
        series1dataSource: null as any,
        series1dataPointField: '',
        series1dateField: '',
        series2label: '',
        series2dataSource: null as any,
        series2dataPointField: '',
        series2dateField: '',
        series3label: '',
        series3dataSource: null as any,
        series3dataPointField: '',
        series3dateField: '',
        series4label: '',
        series4dataSource: null as any,
        series4dataPointField: '',
        series4dateField: '',
        categoryRangeUnit: 1,
        categoryRangePeriod: PanelTimePeriod.MONTHS,
        series1threshold: '',
        series2threshold: '',
        series3threshold: '',
        series4threshold: '',
        columnRangeCategory: 'NUMBER',
    },
    modelParams: {
        type: null as any,
        minimumDataMovementScore: 3,
        triThreshold1Type: PanelThresholdType.SAMPLE_MEDIAN,
        thresholdNum1: 0,
        triThreshold2Type: PanelThresholdType.SAMPLE_MEAN,
        thresholdNum2: 0,
        stableValue: 0,
        dataInclusionUnit: 1,
        dataInclusionPeriod: PanelTimePeriod.MONTHS,
        studyComparison: PanelAggregate.MEAN,
        unitMultiplier: 1,
        amberCutoff: 0.05,
        redCutoff: 0.025,
        siteScoring: PanelSiteScoring.STANDARD,
        confidenceIntervalType: PanelIntervalType.APPROXIMATE,
        hideLower: false,
        hideUpper: false,
        show: 'all',
        upperAmber: 95.5,
        lowerAmber: 95.5,
        upperRed: 98.9,
        lowerRed: 98.9,
        upperBigRed: 99.8,
        lowerBigRed: 99.8,
        benchmarkType: PanelBenchmark.STUDY_MEAN,
        benchmarkValue: 0,
        plotBy: PanelPlotBy.MONTHS,
        rateBy: PanelRateBy.MONTHS,
        greenMaxScore: 25,
        amberMaxScore: 50,
        rateGroupType: null as any,
    },
    dataSource: {
        dataSource: null as any,
        yAxisDataField: '',
        sortField: '',
        countField: '',
        rateByField: '',
        rateByType: PanelRateByType.COUNT,
        timePeriod: PanelTimePeriod.DAYS,
        xAxisDataField: '',
        dateField: '',
        flagField: '',
        countFirstEventTypeOnly: false,
        useEarliestEndDate: false,
        xAxisEndDateFields: [] as any,
    },
    studyOverviewExtraColumns: [],
    scheduleId: '',
    yAxisColumnValue: '',
});
